import fp from "lodash/fp";

import {
	ACHProcessorData as ach,
	AchFeesFields,
	AriseFeesFields,
} from "globalConstants";
import { FeeProgramTypeByValue } from "helpers";
import {
	AmexOptBlueOrEsaTypesByValue,
	RefundPolicyTypes,
	CreditDebitByValueTypeCartTypes,
	TypeOfBusinessValues,
} from "types";

const checkBoolean = item => {
	return item === "true" || item === true;
};

const mapAddress = (address: { [key: string]: any } | undefined) => ({
	address_line1: address?.route,
	address_line2: address?.apartments,
	city: address?.locality,
	region_code: address?.administrative_area_level_1,
	postal_code: address?.postal_code,
	country: address?.country?.displayName,
	type_of_location: address?.type_of_location,
	location_if_other: address?.location_if_other,
});

const applicationSummaryMapper = ({
	processorForm,
	businessForm,
	ownerPrincipalForm,
	softwareOptionsGeneralForm,
	softwareOptionsFileBuildOnlyForm,
	softwareOptionsBillingInformationForm,
	feesForm,
	additionalServicesForm,
	dealNotesForm,
}: {
	processorForm: { [key: string]: any };
	businessForm: { [key: string]: any };
	ownerPrincipalForm: { [key: string]: any };
	softwareOptionsGeneralForm: { [key: string]: any };
	softwareOptionsFileBuildOnlyForm: { [key: string]: any };
	softwareOptionsBillingInformationForm: { [key: string]: any };
	feesForm: { [key: string]: any };
	additionalServicesForm: { [key: string]: any };
	dealNotesForm: { [key: string]: any };
}) => {
	return {
		// Processor / Sponsor Info
		primaryAgent: processorForm?.primaryAgent,
		headquarters: processorForm?.headquarters,
		processor: processorForm?.processorSponsorSection?.processor,
		sponsor: processorForm?.processorSponsorSection?.sponsor,
		achProcessor: processorForm?.processorSponsorSection?.achProcessor,
		mccCode: processorForm?.mccCodeSection?.mccCode,
		whatDoYouSell: processorForm?.mccCodeSection?.whatDoYouSell,
		detailedExplanationWhatDoYouSell:
			processorForm?.mccCodeSection?.detailedExplanationWhatDoYouSell,
		scheduleA: processorForm?.residualsSection?.scheduleA,
		splitProfile: processorForm?.residualsSection?.splitProfile,

		// Business Info
		merchantType: businessForm?.merchantType,
		dbaName: businessForm?.dbaName,
		legalName: businessForm?.legalName,
		website: businessForm?.website,
		fax: businessForm?.fax,
		businessPhone: businessForm?.businessPhone,
		csPhone: businessForm?.csPhone,
		businessEmail: businessForm?.businessEmail,
		isMerchantOptsOutOfReceivingFutureCommercialFromAmex:
			businessForm?.isMerchantOptsOutOfReceivingFutureCommercialFromAmex,
		typeOfBusiness: TypeOfBusinessValues.getByName(
			businessForm?.typeOfBusiness,
		)?.value,
		noOfLocations: businessForm?.noOfLocations,
		businessEstablishedDate: businessForm?.businessEstablishedDate,
		businessIfOther: businessForm?.businessIfOther,
		taxId: businessForm?.taxId,
		businessAddress: businessForm?.businessAddress?.address
			? mapAddress(businessForm?.businessAddress?.address)
			: businessForm?.businessAddress,
		legalAddress: businessForm?.legalAddress?.address
			? mapAddress(businessForm?.legalAddress?.address)
			: businessForm?.legalAddress,
		documentationMailingAddress: businessForm?.documentationMailingAddress,
		isPrintingPaperStatementEnabled: checkBoolean(
			businessForm?.isPrintingPaperStatementEnabled,
		),
		swiped: businessForm?.swiped,
		keyed: businessForm?.keyed,
		eCommerce: businessForm?.eCommerce,
		monthlyVMCD: businessForm?.monthlyVMCD,
		monthlyAMEX: businessForm?.monthlyAMEX,
		achProcessorData: {
			[ach.MonthlyVolumeAch.beName]:
				businessForm?.[ach.MonthlyVolumeAch.name],
			[ach.MonthlyVolumeAchPayouts.beName]:
				businessForm?.[ach.MonthlyVolumeAchPayouts.name],
			[ach.TicketCountAch.beName]:
				businessForm?.[ach.TicketCountAch.name],
			[ach.TicketCountAchPayouts.beName]:
				businessForm?.[ach.TicketCountAchPayouts.name],
			[ach.HighTicketAch.beName]: businessForm?.[ach.HighTicketAch.name],
			[ach.HighTicketAchPayouts.beName]:
				businessForm?.[ach.HighTicketAchPayouts.name],
			[ach.CustomerType.beName]: businessForm?.[ach.CustomerType.name],
			[ach.PaymentAuthorizationType.beName]:
				businessForm?.[ach.PaymentAuthorizationType.name],
			[ach.StatementDescriptor.beName]:
				businessForm?.[ach.StatementDescriptor.name],
			[ach.UseOfPaymentServiceDescription.beName]:
				businessForm?.[ach.UseOfPaymentServiceDescription.name],
			[ach.BillingRouting.beName]:
				businessForm?.[ach.BillingRouting.name],
			[ach.BillingBank.beName]: businessForm?.[ach.BillingBank.name],
			[ach.BillingAccount.beName]:
				businessForm?.[ach.BillingAccount.name],
			[ach.BillingAccountName.beName]:
				businessForm?.[ach.BillingAccountName.name],
			[ach.IsApplyBankAccountData.beName]: checkBoolean(
				businessForm?.[ach.IsApplyBankAccountData.name],
			),
		},
		excludeCardTypes: businessForm?.excludeCardTypes
			? Object.keys(businessForm.excludeCardTypes)
			: [],
		avgTicketVMCD: businessForm?.avgTicketVMCD,
		avgTicketAMEX: businessForm?.avgTicketAMEX,
		highTicketVMCD: businessForm?.highTicketVMCD,
		highTicketAMEX: businessForm?.highTicketAMEX,
		seasonalChecked: businessForm?.seasonalChecked,
		seasonalMonths: businessForm?.seasonalMonths?.map(({ value }) => value),
		refundPolicy: RefundPolicyTypes.getByName(businessForm?.refundPolicy)
			?.value,
		refundPolicyIfOther: businessForm?.refundPolicyIfOther,
		creditAndDebitCardTransactionsAreSettled:
			CreditDebitByValueTypeCartTypes.getByName(
				businessForm?.creditAndDebitCardTransactionsAreSettled,
			)?.value,
		creditDebitCardTransactionsAreSettledTypeIfOther:
			businessForm?.creditDebitCardTransactionsAreSettledTypeIfOther,
		sellingOutsideOfUS: checkBoolean(businessForm?.sellingOutsideOfUS),
		haveYouAcceptedCardsPreviosly: checkBoolean(
			businessForm?.haveYouAcceptedCardsPreviously,
		),
		previousAccountMIDNumber: businessForm?.previousAccountMIDNumber,
		whoWasPreviousProcessor: businessForm?.whoWasPreviousProcessor,
		minDaysToDelivery: businessForm?.minDaysToDelivery,
		avgDaysToDelivery: businessForm?.avgDaysToDelivery,
		maxDaysToDelivery: businessForm?.maxDaysToDelivery,
		recurringPayments: checkBoolean(businessForm?.recurringPayments),
		doYouUseAThirdPartyForFulfillment: checkBoolean(
			businessForm?.doYouUseAThirdPartyForFulfillment,
		),
		whoDoYouUseAThirdPartyForFulfillment:
			businessForm?.whoDoYouUseAThirdPartyForFulfillment,
		siteInspectionBy: businessForm?.siteInspectionBy,
		routing: businessForm?.routing,
		bank: businessForm?.bank,
		account: businessForm?.account,
		accountName: businessForm?.accountName,

		// Owner Principal Info
		owner1: {
			...ownerPrincipalForm?.ownerPrincipalSection?.owners[0],
			address: mapAddress(
				ownerPrincipalForm?.ownerPrincipalSection?.owners[0]?.address,
			),
		},
		owner2: ownerPrincipalForm?.ownerPrincipalSection?.owners[1]
			? {
					...ownerPrincipalForm?.ownerPrincipalSection?.owners[1],
					address: mapAddress(
						ownerPrincipalForm?.ownerPrincipalSection?.owners[1]
							?.address,
					),
			  }
			: null,
		owner3: ownerPrincipalForm?.ownerPrincipalSection?.owners[2]
			? {
					...ownerPrincipalForm?.ownerPrincipalSection?.owners[2],
					address: mapAddress(
						ownerPrincipalForm?.ownerPrincipalSection?.owners[2]
							?.address,
					),
			  }
			: null,
		owner4: ownerPrincipalForm?.ownerPrincipalSection?.owners[3]
			? {
					...ownerPrincipalForm?.ownerPrincipalSection?.owners[3],
					address: mapAddress(
						ownerPrincipalForm?.ownerPrincipalSection?.owners[3]
							?.address,
					),
			  }
			: null,
		authorizedSigner: !fp.isEmpty(
			ownerPrincipalForm?.ownerPrincipalSection?.signer,
		)
			? {
					...ownerPrincipalForm?.ownerPrincipalSection?.signer,
					address: mapAddress(
						ownerPrincipalForm?.ownerPrincipalSection?.signer
							?.address,
					),
			  }
			: null,
		primaryContact: checkBoolean(
			ownerPrincipalForm?.isPrimaryContactTheSame,
		)
			? {
					...ownerPrincipalForm?.ownerPrincipalSection?.owners[0],
					address: mapAddress(
						ownerPrincipalForm?.ownerPrincipalSection?.owners[0]
							?.address,
					),
					phone: ownerPrincipalForm?.ownerPrincipalSection?.owners[0]
						?.homeMobilePhone,
					email: ownerPrincipalForm?.ownerPrincipalSection?.owners[0]
						?.emailAddress,
			  }
			: {
					firstName: ownerPrincipalForm?.firstName,
					lastName: ownerPrincipalForm?.lastName,
					phone: ownerPrincipalForm?.phone,
					email: ownerPrincipalForm?.email,
					titlePosition: ownerPrincipalForm?.titlePosition,
			  },

		// Software Info
		softwareOptionDefaults: {
			bill_to: +softwareOptionsBillingInformationForm?.billTo,
			file_build_only_quantity:
				softwareOptionsFileBuildOnlyForm?.fileBuildOnlyQuantity,
			file_build_only_var_recipient_email:
				softwareOptionsFileBuildOnlyForm?.fileBuildOnlyVarRecipientEmail,
			installation: {
				terminal_install:
					softwareOptionsGeneralForm?.installation?.terminalInstall,
				best_time_to_call:
					softwareOptionsGeneralForm?.installation?.bestTimeToCall,
				contact_first_name:
					softwareOptionsGeneralForm?.installation?.contactFirstName,
				contact_last_name:
					softwareOptionsGeneralForm?.installation?.contactLastName,
				contact_phone_number:
					softwareOptionsGeneralForm?.installation
						?.contactPhoneNumber,
				contact_email:
					softwareOptionsGeneralForm?.installation?.contactEmail,
				notes: softwareOptionsGeneralForm?.installation?.notes,
			},
			payment_method_id:
				softwareOptionsBillingInformationForm?.paymentMethodId,
			payment_method_preview: {
				is_deleted: false,
			},
			payment_type: softwareOptionsBillingInformationForm?.paymentType,
			shipping: {
				shipping_method:
					softwareOptionsGeneralForm?.shipping?.shippingMethod,
				address: mapAddress(
					softwareOptionsGeneralForm?.shipping?.address,
				),
				phone_number: softwareOptionsGeneralForm?.shipping?.phoneNumber,
				should_copy_shipping_address_to_merchant:
					softwareOptionsGeneralForm?.shipping
						?.shouldCopyShippingAddressToMerchant,
			},
			zero_cost_program_type:
				softwareOptionsGeneralForm?.zeroCostProgramType,
			zero_cost_amount: softwareOptionsGeneralForm?.zeroCostAmount,
		},

		// Fees
		feeProgram: FeeProgramTypeByValue(feesForm?.feeProgram),
		feeTemplate: feesForm?.feeTemplate,
		riseFreeProgramFee: null,

		// Interchange Plus
		interchangePlusVMCD: feesForm?.interchangePlusVMCD,
		interchangePlusOfflineDebit: feesForm?.interchangePlusOfflineDebit,
		interchangePlusAmex: feesForm?.interchangePlusAmex,
		commercialCardInterchangeServiceFee:
			feesForm?.commercialCardInterchangeServiceFee,
		passCardBrandFees: checkBoolean(feesForm?.passCardBrandFees),
		interchangePlusRevenueType: feesForm?.interchangePlusRevenueType,

		// Flat Rate
		flatRateIsRiseFree: checkBoolean(feesForm?.flatRateIsRiseFree),
		flatRate: feesForm?.flatRate,

		// Three Tier
		threeTierQualVMCD: feesForm?.threeTierQualVMCD || 0,
		threeTierQualAmex: feesForm?.threeTierQualAmex || 0,
		threeTierQualOfflineDebit: feesForm?.threeTierQualOfflineDebit || 0,
		threeTierMidQualVMCD: feesForm?.threeTierMidQualVMCD || 0,
		threeTierMidQualAmex: feesForm?.threeTierMidQualAmex || 0,
		threeTierMidQualOfflineDebit:
			feesForm?.threeTierMidQualOfflineDebit || 0,
		threeTierNonQualVMCD: feesForm?.threeTierNonQualVMCD || 0,
		threeTierNonQualAmex: feesForm?.threeTierNonQualAmex || 0,
		threeTierNonQualOfflineDebit:
			feesForm?.threeTierNonQualOfflineDebit || 0,
		isPassThroughInterchange: checkBoolean(
			feesForm?.isPassThroughInterchange,
		),

		// ERR
		errQualVMCD: feesForm?.errQualVMCD || 0,
		errQualAmex: feesForm?.errQualAmex || 0,
		errQualOfflineDebit: feesForm?.errQualOfflineDebit || 0,
		errNonQualVMCD: feesForm?.errNonQualVMCD || 0,
		errNonQualAmex: feesForm?.errNonQualAmex || 0,
		errNonQualOfflineDebit: feesForm?.errNonQualOfflineDebit || 0,

		// Additional Fees
		authorizationFee: feesForm?.authorizationFee || 0,
		avs: feesForm?.avs || 0,
		aru: feesForm?.aru || 0,
		batchSettlement: feesForm?.batchSettlement || 0,
		retrievalFee: feesForm?.retrievalFee || 0,
		salesTransactionFee: feesForm?.salesTransactionFee || 0,
		voiceAuth: feesForm?.voiceAuth || 0,
		otherVolume: feesForm?.otherVolume || 0,
		riseFreeTransFee: null,
		additionalFeeOtherItemFee: feesForm?.additionalFeeOtherItemFee || 0,
		voiceAVS: feesForm?.voiceAVS || 0,
		returnTransactionFee: feesForm?.returnTransactionFee || 0,
		applicationFee: feesForm?.applicationFee || 0,
		earlyTerminationFee: feesForm?.earlyTerminationFee || 0,
		annualFee: feesForm?.annualFee || 0,
		monthAnnualFeeBilled: +feesForm?.monthAnnualFeeBilled,
		gatewayPerAuth: feesForm?.gatewayPerAuth || 0,
		gatewayAdjustmentsFee: feesForm?.gatewayAdjustmentsFee || 0,
		gatewayDepositFee: feesForm?.gatewayDepositFee || 0,
		ebtAccountNumber: feesForm?.ebtAccountNumber,
		ebtCashPerItemFee: feesForm?.ebtCashPerItemFee || 0,
		ebtFoodPerItemFee: feesForm?.ebtFoodPerItemFee || 0,
		discountType: +feesForm?.discountType,
		isMonthEnd: checkBoolean(feesForm?.isMonthEnd),

		// Monthly Fees
		customerServiceFee: feesForm?.customerServiceFee || 0,
		onlineReportingFee: feesForm?.onlineReportingFee || 0,
		regulatoryProductFee: feesForm?.regulatoryProductFee || 0,
		softwareFee: feesForm?.softwareFee || 0,
		wirelessFee: feesForm?.wirelessFee || 0,
		statementFee: feesForm?.statementFee || 0,
		equipmentFee: feesForm?.equipmentFee || 0,
		pciComplianceFee: feesForm?.pciComplianceFee || 0,
		toBeBilled: +feesForm?.toBeBilled || 0,
		pciNonCompilance: feesForm?.pciNonCompilance || 0,
		monthPCIAnnualFeeCharged: +feesForm?.monthPCIAnnualFeeCharged || 0,
		monthlyMinimumFee: feesForm?.monthlyMinimumFee || 0,
		tinInvalidFee: feesForm?.tinInvalidFee || 0,
		achRejectFee: feesForm?.achRejectFee || 0,
		merchantMonitoringFee: feesForm?.merchantMonitoringFee,
		inactiveFee: feesForm?.inactiveFee || 0,
		chargebackFee: feesForm?.chargebackFee || 0,
		isPinDebitEnabled: checkBoolean(feesForm?.isPinDebitEnabled),
		pinDebitSponsorship: checkBoolean(feesForm?.pinDebitSponsorship),
		monthlyAccessFee: feesForm?.monthlyAccessFee || 0,
		otherVolumeFee: feesForm?.otherVolumeFee || 0,
		authorizationFeeOtherItemFee:
			feesForm?.authorizationFeeOtherItemFee || 0,
		voyagerQual: feesForm?.voyagerQual || 0,
		voyagerOtherItemRate: feesForm?.voyagerOtherItemRate || 0,
		wexQual: feesForm?.wexQual || 0,
		wexOtherItemRate: feesForm?.wexOtherItemRate || 0,
		amexType: feesForm?.amexType
			? AmexOptBlueOrEsaTypesByValue.getByName(
					feesForm?.isActiveAmexOptBlueOrEsaSection
						? feesForm?.amexType
						: AmexOptBlueOrEsaTypesByValue.None.name,
			  )?.value
			: null,
		amexESANumber: feesForm?.amexESANumber,
		amexOtherItemFee: feesForm?.amexOtherItemFee || 0,
		amexOtherVolumeFee: feesForm?.amexOtherVolumeFee || 0,

		// ACH Fees
		[AchFeesFields.SetupFee.name]:
			feesForm?.[AchFeesFields.SetupFee.name] || 0,
		[AchFeesFields.OriginationLargeTransactionDiscountFee.name]:
			feesForm?.[
				AchFeesFields.OriginationLargeTransactionDiscountFee.name
			] || 0,
		[AchFeesFields.AccountVerificationFee.name]:
			feesForm?.[AchFeesFields.AccountVerificationFee.name] || 0,
		[AchFeesFields.MonthlyFee.name]:
			feesForm?.[AchFeesFields.MonthlyFee.name] || 0,
		[AchFeesFields.StandardReturnFee.name]:
			feesForm?.[AchFeesFields.StandardReturnFee.name] || 0,
		[AchFeesFields.AccountVerificationPlusFee.name]:
			feesForm?.[AchFeesFields.AccountVerificationPlusFee.name] || 0,
		[AchFeesFields.TransactionFee.name]:
			feesForm?.[AchFeesFields.TransactionFee.name] || 0,
		[AchFeesFields.NoticeOfChangeReturnFee.name]:
			feesForm?.[AchFeesFields.NoticeOfChangeReturnFee.name] || 0,
		[AchFeesFields.OriginationDiscountFee.name]:
			feesForm?.[AchFeesFields.OriginationDiscountFee.name] || 0,
		[AchFeesFields.ChargebackReturnFee.name]:
			feesForm?.[AchFeesFields.ChargebackReturnFee.name] || 0,

		// ARISE Fees
		[AriseFeesFields.PlatformSetupFee.name]:
			feesForm?.[AriseFeesFields.PlatformSetupFee.name] || 0,
		[AriseFeesFields.PlatformMonthlyFee.name]:
			feesForm?.[AriseFeesFields.PlatformMonthlyFee.name] || 0,
		[AriseFeesFields.CustomerVaultMonthlyFee.name]:
			feesForm?.[AriseFeesFields.CustomerVaultMonthlyFee.name] || 0,
		[AriseFeesFields.EnhancedDataTransactionFee.name]:
			feesForm?.[AriseFeesFields.EnhancedDataTransactionFee.name] || 0,
		[AriseFeesFields.CardTransactionAuthorizationFee.name]:
			feesForm?.[AriseFeesFields.CardTransactionAuthorizationFee.name] ||
			0,
		[AriseFeesFields.ECommercePluginSetupFee.name]:
			feesForm?.[AriseFeesFields.ECommercePluginSetupFee.name] || 0,
		[AriseFeesFields.ECommercePluginMonthlyFee.name]:
			feesForm?.[AriseFeesFields.ECommercePluginMonthlyFee.name] || 0,
		[AriseFeesFields.EMVDeviceSetupFee.name]:
			feesForm?.[AriseFeesFields.EMVDeviceSetupFee.name] || 0,
		[AriseFeesFields.AdditionalEmvDeviceMonthlyFee.name]:
			feesForm?.[AriseFeesFields.AdditionalEmvDeviceMonthlyFee.name] || 0,
		[AriseFeesFields.EnhancedDataSetupFee.name]:
			feesForm?.[AriseFeesFields.EnhancedDataSetupFee.name] || 0,
		[AriseFeesFields.CustomerVaultSetupFee.name]:
			feesForm?.[AriseFeesFields.CustomerVaultSetupFee.name] || 0,
		[AriseFeesFields.CustomerVaultAutomaticUpdateFee.name]:
			feesForm?.[AriseFeesFields.CustomerVaultAutomaticUpdateFee.name] ||
			0,
		[AriseFeesFields.MobileInvoicingPaymentsSetupFee.name]:
			feesForm?.[AriseFeesFields.MobileInvoicingPaymentsSetupFee.name] ||
			0,
		[AriseFeesFields.MobileInvoicingPaymentsMonthlyFee.name]:
			feesForm?.[
				AriseFeesFields.MobileInvoicingPaymentsMonthlyFee.name
			] || 0,
		[AriseFeesFields.MobileInvoicingPaymentsMessageFee.name]:
			feesForm?.[
				AriseFeesFields.MobileInvoicingPaymentsMessageFee.name
			] || 0,
		[AriseFeesFields.CardNetworkTokenizationSetupFee.name]:
			feesForm?.[AriseFeesFields.CardNetworkTokenizationSetupFee.name] ||
			0,
		[AriseFeesFields.CardNetworkTokenizationMonthlyFee.name]:
			feesForm?.[
				AriseFeesFields.CardNetworkTokenizationMonthlyFee.name
			] || 0,
		[AriseFeesFields.CardNetworkPerTokenizationEventFee.name]:
			feesForm?.[
				AriseFeesFields.CardNetworkPerTokenizationEventFee.name
			] || 0,

		// Misc Fees
		miscFees: feesForm?.miscFees?.map(mf => ({
			value: +(mf.value || 0),
			percentage: +(mf.percentage || 0),
			type: +(mf.type || 0),
			month: mf.month,
			startDate: mf.startDate,
			endDate: mf.endDate,
			description: mf.description,
		})),

		// Additional Services
		ach: checkBoolean(additionalServicesForm?.ach),
		cashAdvance: checkBoolean(additionalServicesForm?.cashAdvance),
		multiCurrency: checkBoolean(additionalServicesForm?.multiCurrency),
		businessConnections: checkBoolean(
			additionalServicesForm?.businessConnections,
		),
		checkServices: checkBoolean(additionalServicesForm?.checkServices),
		giftLoyalty: checkBoolean(additionalServicesForm?.giftLoyalty),
		existingProvider: additionalServicesForm?.existingProvider,
		existingAcctNumber: additionalServicesForm?.existingAcctNumber,
		nailSoftGiftLoyalty: checkBoolean(
			additionalServicesForm?.nailSoftGiftLoyalty,
		),
		bundles: checkBoolean(additionalServicesForm?.bundles),
		bundle1: additionalServicesForm?.bundle1,
		priceBundle1: additionalServicesForm?.priceBundle1,
		bundle2: additionalServicesForm?.bundle2,
		priceBundle2: additionalServicesForm?.priceBundle2,
		noteBundle: additionalServicesForm?.noteBundle,

		// Deal Notes
		dealNotesCount: dealNotesForm?.dealNotesCount,
		lastDealNote: dealNotesForm?.lastDealNote,
	};
};

export { applicationSummaryMapper };
